import { CommonLocale, HelperLocales, TeacherClassError, TeacherClassLocale, TeamLocales } from "@/locales/localeid";
import { RemoteTeachingService } from "@/services";
import { mapObjectsFromBE } from "@/store/annotation-v2/actions";
import { ExposureStatus } from "@/store/lesson/state";
import { StudyMode } from "@/store/teaching/interfaces";
import { MODULES, vuexName } from "@/store/utils";
import { MIN_ZOOM_RATIO, SESSION_ID } from "@/utils/constant";
import { calculatePlayedTime } from "@/utils/convertDuration";
import { Logger } from "@/utils/logger";
import { notifyStudentMovedAppToBackground } from "@/utils/notifications";
import { HelperJoiningNotify } from "@/views/teacher-class/components";
import {
  createRealtimeHandlerKey,
  HelperAction,
  HelperEnterClassEventData,
  HelperRequestToJoinSessionEventData,
  LessonActionEventData,
  OneToOneRealtimeEventData,
  REALTIME_EVENT,
  RealtimeHandlerMap,
  SessionAction,
  StudentAction,
  StudentEnterClassEventData,
  StudentLeaveClassEventData,
  StudentMovedAppToBackgroundEventData,
  StudentPaletteEventData,
  StudentRaisingHandEventData,
  StudentUpdateIndependentExposureEventData,
  TeacherAction,
  TeacherClearRaisingHandEventData,
  TeacherEndExposureEventData,
  TeacherMoveZoomedSlideEventData,
  TeacherScrollPdfEventData,
  TeacherUpdateAnnotationModeEventData,
  TeacherUpdateCanvasObjectEventData,
  TeacherUpdateExposureEventData,
  TeacherUpdateMediaStateEventData,
  TeacherUpdateStudentsBadgeEventData,
  TeacherZoomSlideEventData,
  TeamAction,
  ToggleHelperVideoEventData,
  ToggleIndependentModeEventData,
  ToggleTargetEventData,
  ToggleTeamModeEventData,
  WhiteboardAction,
} from "@/ws";
import { Modal, notification } from "ant-design-vue";
import { h } from "vue";
import { fmtMsg } from "vue-glcommonui";
import { ActionContext } from "vuex";
import { ClassActionFromValue } from "../student/state";
import { TeacherRoomState } from "./state";

export const useTeacherRealtimeHandler = ({ commit, rootGetters, state, dispatch }: ActionContext<TeacherRoomState, any>): RealtimeHandlerMap => {
  const handlerMap = new Map<string, (data: any, sender: string) => void | Promise<void>>();
  const myUserId = rootGetters[vuexName(MODULES.AUTH.GETTERS.GET_USER_ID)];
  const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];

  const timestamps = {
    onTeacherUpdateExposureTimestamp: 0,
  };

  // #region TEACHER MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.ENTER_CLASS), async (_: any) => {
    // do nothing
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_STUDENT_BADGE),
    async (data: TeacherUpdateStudentsBadgeEventData) => {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENTS_BADGE, false), data);
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_STUDENT_PALETTE), async (data: StudentPaletteEventData) => {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), data);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.SESSION, SessionAction.UPDATE_LESSON_AND_UNIT), async (teacherId: string) => {
    commit(
      vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
      {
        objects: mapObjectsFromBE([]),
      },
      { root: true },
    );
    if (myUserId === teacherId) return;
    await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_LESSON_AND_UNIT, false), null);
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_MEDIA_STATE),
    async (data: TeacherUpdateMediaStateEventData) => {
      if (!isHelper) return;
      const { startMediaAt, duration, pauseMediaAt } = data;
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_MEDIA_STATE), { startMediaAt, duration, pauseMediaAt }, { root: true });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.REMOVE_HELPER), async () => {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_INFO, false), undefined);
    if (isHelper) {
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_IS_HELPER), false, { root: true });
      return;
    }
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
    notification.info({
      message: fmtMsg(TeacherClassLocale.RemoveHelperSuccessfully),
    });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.CLEAR_RAISING_HAND),
    async ({ studentId }: TeacherClearRaisingHandEventData) => {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
        id: studentId,
        raisingHand: false,
      });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.TOGGLE_HELPER_VIDEO), async (data: ToggleHelperVideoEventData) => {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_VIDEO_STATUS, false), !!data?.show);
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.TOGGLE_INDEPENDENT_MODE),
    async (data: ToggleIndependentModeEventData) => {
      if (!isHelper) return;
      await dispatch(vuexName(MODULES.TEACHER_TEACHING.DISPATCHES.HELPER_TOGGLE_INDEPENDENT_MODE), data?.firstItemId ?? null, { root: true });
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_EXPOSURE),
    async (data: TeacherUpdateExposureEventData, sender: string) => {
      if (sender === myUserId) return;

      const previousTimestamp = timestamps.onTeacherUpdateExposureTimestamp;
      if (previousTimestamp && data.timestamp < previousTimestamp) return;
      timestamps.onTeacherUpdateExposureTimestamp = data.timestamp;

      //set current exposure
      if (data.exposureId) {
        commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE), { id: data.exposureId }, { root: true });
      }

      //set current exposure item
      if (isHelper && !data.exposureItemId) commit(vuexName(MODULES.LESSON.COMMITS.END_CURRENT_CONTENT), {}, { root: true });
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), false, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: data.exposureItemId }, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_ZOOM_STATE), null, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), 0, { root: true });
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.RESET_STATE), null, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: 0, y: 0 }, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), MIN_ZOOM_RATIO, { root: true });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD, false), false);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS), [], { root: true });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.END_EXPOSURE), async (data: TeacherEndExposureEventData) => {
    if (data.needUpdateRemainingTime) {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_EXPOSURE_STATUS), { id: data.exposureId, status: ExposureStatus.COMPLETED }, { root: true });
      const playedTime = calculatePlayedTime(rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_EXPOSURES)]);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME), { time: playedTime }, { root: true });
    } else {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_EXPOSURE_STATUS), { id: data.exposureId }, { root: true });
    }
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_ONE_ONE), async (data: OneToOneRealtimeEventData) => {
    const oneToOneTargetStudentId = data?.studentId || "";
    const isEnteringOneToOne = data?.enable;
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];

    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), null, { root: true });

    if (!oneToOneTargetStudentId && isEnteringOneToOne) return;

    if (isHelper) {
      commit(
        vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID),
        { id: isEnteringOneToOne ? oneToOneTargetStudentId : "" },
        { root: true },
      );
      return;
    }

    if (isEnteringOneToOne) {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: oneToOneTargetStudentId }, { root: true });
      await state.manager?.switchChannels();
    } else {
      try {
        const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
        if (!sessionId || !oneToOneTargetStudentId) return;

        const { data: sessionData } = await RemoteTeachingService.getMinimalSessionDto(sessionId, oneToOneTargetStudentId);

        // Set flag to prevent auto-clearing of targets during state restoration
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), true, { root: true });

        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
        commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), sessionData.isTeamMode, { root: true });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "" }, { root: true });
        await state.manager?.switchChannels();
        commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHING_MODE, false), sessionData.teachingMode);
        commit(vuexName(MODULES.LESSON.COMMITS.END_CURRENT_CONTENT), {}, { root: true });
        commit(
          vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
          {
            id: sessionData.selectedExposureId,
            preventSelectFirstSlideAutomatically: !!sessionData.selectedExposureItemId,
          },
          { root: true },
        );
        if (sessionData.selectedExposureItemId) {
          commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: sessionData.selectedExposureItemId }, { root: true });
        }
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD), sessionData.isShowWhiteBoard, { root: true });
        await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API), sessionData.drawing?.targets, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), sessionData.ratio, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), sessionData.ratioScrollPdf, { root: true });
        if (sessionData.position) {
          commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: sessionData.position.x, y: sessionData.position.y }, { root: true });
        }

        // Update independent mode
        if (sessionData.independentMode) {
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
          commit(
            vuexName(MODULES.TEACHER_TEACHING.COMMITS.SET_INDEPENDENT_LIST_STUDENT_ITEMS),
            rootGetters[vuexName(MODULES.TEACHER_TEACHING.GETTERS.GET_INDEPENDENT_LIST_STUDENT_ITEMS)],
            { root: true },
          );
        } else {
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
          commit(vuexName(MODULES.TEACHER_TEACHING.COMMITS.SET_INDEPENDENT_LIST_STUDENT_ITEMS), null, { root: true });
        }
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
          studentId: oneToOneTargetStudentId,
          isPaletteEnabled: sessionData.isPalette,
        });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION, false), {
          action: ClassActionFromValue(sessionData.lessonAction),
        });

        commit(
          vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
          {
            objects: mapObjectsFromBE(sessionData.drawing?.objects || []),
          },
          { root: true },
        );

        // Reset flag after restoration is complete
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      } catch (err) {
        Logger.log(err);
        // Reset flag in case of error
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      }
    }
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.FAILED_SET_ONE_ONE), async () => {
    if (isHelper) return;
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "" }, { root: true });
    await state.manager?.switchChannels();
    notification.error({
      message: fmtMsg(TeacherClassError.FailSetOneOneMode),
    });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_WHITEBOARD), async (isShowWhiteBoard: boolean) => {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD), isShowWhiteBoard, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_LESSON_ACTION), async (data: LessonActionEventData) => {
    commit(
      vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION),
      {
        action: ClassActionFromValue(data.action),
      },
      { root: true },
    );
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_CANVAS_OBJECT),
    async (data: TeacherUpdateCanvasObjectEventData) => {
      const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
      if (!isHelper) return;

      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_NEXT_OBJECT_TOBE_UPDATED), data, { root: true });
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_ANNOTATION_MODE),
    async (data: TeacherUpdateAnnotationModeEventData) => {
      if (!isHelper) return;
      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_ANNOTATION_MODE), data.mode, { root: true });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SCROLL_PDF), async (data: TeacherScrollPdfEventData) => {
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    if (!isHelper) return;
    commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), data, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.ZOOM_SLIDE), async (data: TeacherZoomSlideEventData) => {
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    if (!isHelper) return;
    commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), data.ratio, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: data.position.x, y: data.position.y }, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.MOVE_ZOOMED_SLIDE), async (data: TeacherMoveZoomedSlideEventData) => {
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    if (!isHelper) return;
    const { x, y } = data;
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x, y }, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_TEACHING_MODE), async (data: { mode: number }) => {
    if (!rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)]) return;
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHING_MODE, false), data.mode);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.END_SESSION), async () => {
    const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
    localStorage.setItem(SESSION_ID, sessionId);
    if (isHelper) {
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_IS_HELPER), false, { root: true });
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS, false));
    }
  });

  // #endregion

  // #region STUDENT MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.ENTER_CLASS), async (data: StudentEnterClassEventData) => {
    const { id, isRaisingHand, isPalette } = data;
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
      id,
      raisingHand: isRaisingHand,
    });
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
      studentId: id,
      isPaletteEnabled: isPalette,
    });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.RAISING_HAND),
    async (data: StudentRaisingHandEventData, sender: string) => {
      if (sender === myUserId) return;
      const studentId = sender;
      const raisingHand = data.raisingHand;

      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
        id: studentId,
        raisingHand,
      });
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.LEAVE_SESSION),
    async (data: StudentLeaveClassEventData, sender: string) => {
      const studentId = data?.studentId || sender;
      if (!studentId) return;
      const idOne = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];
      if (isHelper && idOne && idOne === studentId) {
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE, false));
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_LEFT_CLASS, false), { id: studentId });
      const student = state.students.find((st) => st.id === studentId);
      if (!state.isSessionEnded && student?.englishName) {
        notification.warn({
          message: fmtMsg(TeacherClassLocale.StudentLeftClass, {
            studentName: student.englishName,
          }),
        });
      }
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.UPDATE_INDEPENDENT_EXPOSURE_ITEM),
    async (payload: StudentUpdateIndependentExposureEventData) => {
      commit(vuexName(MODULES.TEACHER_TEACHING.COMMITS.UPDATE_INDEPENDENT_LIST_STUDENT_ITEMS), payload, { root: true });
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.STUDENT_MOVED_APP_TO_BACKGROUND),
    async (payload: StudentMovedAppToBackgroundEventData) => {
      const student = state.students.find((student) => student.id === payload.studentId);
      notifyStudentMovedAppToBackground(student?.englishName || "");
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.ENTER_CLASS), async (data: HelperEnterClassEventData) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_INFO, false), data);
  });

  // #endregion

  // #region WHITEBOARD MODULE HANDLERS

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.WHITEBOARD, WhiteboardAction.TOGGLE_TARGET),
    async (data: ToggleTargetEventData, sender: string) => {
      if (sender === myUserId) return;

      await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGET_BY_FROM_MESSAGE), data, { root: true });
    },
  );

  // #endregion

  // #region Helper MODULE HANDLERS
  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.SET_ONE_ONE), async (data: OneToOneRealtimeEventData) => {
    const oneToOneTargetStudentId = data?.studentId || "";
    const isEnteringOneToOne = !!data?.enable;

    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), null, { root: true });

    if (!oneToOneTargetStudentId && isEnteringOneToOne) return;
    const isHelperUser = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];

    if (!isHelperUser) {
      commit(
        vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID),
        { id: isEnteringOneToOne ? oneToOneTargetStudentId : "" },
        { root: true },
      );
      await state.manager?.switchChannels();
      return;
    }

    if (isEnteringOneToOne) {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: oneToOneTargetStudentId }, { root: true });
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), true, { root: true });
      await state.manager?.switchChannels();
      return;
    }

    try {
      const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
      if (!sessionId || !oneToOneTargetStudentId) return;

      const { data: sessionData } = await RemoteTeachingService.getMinimalSessionDto(sessionId, oneToOneTargetStudentId);

      // Set flag to prevent auto-clearing of targets during state restoration
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), true, { root: true });

      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
        studentId: oneToOneTargetStudentId,
        isPaletteEnabled: sessionData.isPalette,
      });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION, false), {
        action: ClassActionFromValue(sessionData.lessonAction),
      });
      await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), sessionData.isTeamMode, { root: true });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "" }, { root: true });
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), false, { root: true });
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHING_MODE, false), sessionData.teachingMode);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME), { time: sessionData.playedTime }, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.END_CURRENT_CONTENT), {}, { root: true });
      commit(
        vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
        {
          id: sessionData.selectedExposureId,
          preventSelectFirstSlideAutomatically: true,
        },
        { root: true },
      );
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: sessionData.selectedExposureItemId ?? "" }, { root: true });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD), sessionData.isShowWhiteBoard, { root: true });
      commit(
        vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
        {
          objects: mapObjectsFromBE(sessionData.drawing?.objects || []),
        },
        { root: true },
      );
      await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API), sessionData.drawing?.targets, {
        root: true,
      });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), sessionData.ratio, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), sessionData.ratioScrollPdf, { root: true });
      if (sessionData.position) {
        commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: sessionData.position.x, y: sessionData.position.y }, { root: true });
      }

      if (sessionData.independentMode) {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
        commit(
          vuexName(MODULES.TEACHER_TEACHING.COMMITS.SET_INDEPENDENT_LIST_STUDENT_ITEMS),
          rootGetters[vuexName(MODULES.TEACHER_TEACHING.GETTERS.GET_INDEPENDENT_LIST_STUDENT_ITEMS)],
          { root: true },
        );
      } else {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
        commit(vuexName(MODULES.TEACHER_TEACHING.COMMITS.SET_INDEPENDENT_LIST_STUDENT_ITEMS), null, { root: true });
      }
      await state.manager?.switchChannels();

      // Reset flag after restoration is complete
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
    } catch (err) {
      Logger.log(err);
      // Reset flag in case of error
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
    }
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.FAILED_SET_ONE_ONE), async () => {
    const isHelperUser = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    if (!isHelperUser) return;
    notification.error({
      message: fmtMsg(TeacherClassError.FailSetOneOneMode),
    });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.REQUEST_TO_JOIN_SESSION),
    async (data: HelperRequestToJoinSessionEventData) => {
      const shouldIgnore = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IGNORE_HELPER_REQUEST_JOIN_CLASS)];
      if (shouldIgnore) return;
      const { id, name, deviceId } = data;
      const cb = () => {
        commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), true, { root: true });
      };
      notification.info({
        key: id,
        message: fmtMsg(HelperLocales.NotifyTitle),
        description: h(HelperJoiningNotify, {
          helperName: name,
          helperId: id,
          deviceId,
          cb,
        }),
        duration: null,
        class: "helper-joining-notify",
        placement: "topLeft",
      });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.CANCEL_REQUEST_TO_JOIN_SESSION), async (data: { id: string }) => {
    notification.close(data.id);
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.LEAVE_SESSION), async () => {
    await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE, false));
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_INFO, false), undefined);
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.TOGGLE_TEACHER_AUDIO), async () => {
    // do nothing
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.BECOME_TEACHER), async () => {
    await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE, false));
  });

  // #endregion

  // #region TEAM MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEAM, TeamAction.TOGGLE_TEAM_MODE), async (data: ToggleTeamModeEventData) => {
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), !!data?.isTeamMode, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEAM, TeamAction.UPSERT_TEAM), async (data: { isSuccess: boolean }, sender: string) => {
    const isSettingModalOpen = rootGetters[vuexName(MODULES.TEAMS.GETTERS.GET_IS_SETTING_MODAL_OPEN)];
    const isUpdatedByOther = data.isSuccess && isSettingModalOpen && sender !== myUserId;
    const isFailedByMe = !data.isSuccess && sender === myUserId;

    if (isFailedByMe || isUpdatedByOther) {
      Modal.warning({
        title: fmtMsg(TeamLocales.SettingTeams),
        content: fmtMsg(TeamLocales.TeamUpdatedByAnother),
        okText: fmtMsg(CommonLocale.CommonOkButtonText),
        onOk: async () => {
          await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.ON_SETTING_MODAL_CANCEL), null, { root: true });
        },
      });
    }

    await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
  });

  // #endregion

  return handlerMap;
};
